import { useLocation, useNavigate } from 'react-router-dom';
import {
  SContainer,
  SPage,
  SProfilePictureContainer,
  SRecipientContainer,
} from './RecipientGroups.styles';
import { Layout } from '../../components/Layout/Layout';
import { useEffect, useState } from 'react';
import Loader from '../../components/Loader/Loader';
import { useAppSelector } from '../../hooks';
import { selectUserId } from '../Login/LoginSlice';
import { t } from 'i18next';
import { ProfilePicture } from '../../components/ProfilePicture/ProfilePicture';
import { getSortedAccounts } from './helpers';

export enum RecipientType {
  Groups = 'Groups',
  Users = 'Users',
  Accounts = 'Accounts',
  Contacts = 'Contacts',
}

type RecipientGroupsProps = {
  havePrebuiltLayout?: boolean;
};

export type RecipientListItem = {
  id: number;
  name: string;
  subtitle?: string;
  imagePath?: string;
  parentId?: number;
};

type RecipientGroupsState = {
  state: {
    data: RecipientListItem[];
    recipientsType?: RecipientType;
    prevPath?: string;
  };
};

export const RecipientGroups = ({ havePrebuiltLayout = false }: RecipientGroupsProps) => {
  const location = useLocation() as RecipientGroupsState;
  const navigate = useNavigate();
  const { data = [], recipientsType, prevPath } = location.state;

  const haveImage = [RecipientType.Groups, RecipientType.Users].includes(
    recipientsType as RecipientType
  );
  const authUserId = useAppSelector(selectUserId);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<RecipientListItem[]>([]);

  useEffect(() => {
    initialLoad();
  }, []);

  const getDisplayedName = (item: RecipientListItem) => {
    if (recipientsType === RecipientType.Users && authUserId === item.id) {
      return t('you');
    }

    return item.name;
  };

  const initialLoad = async () => {
    try {
      setLoading(true);
      let itemsList = [...data];
      if (recipientsType === RecipientType.Groups) {
        itemsList.sort((a, b) => a.name?.localeCompare(b?.name));
      } else if (recipientsType === RecipientType.Users) {
        const authUserProfile = [...itemsList].find(
          user => user.id === authUserId
        ) as RecipientListItem;
        const otherUsers = [...itemsList].filter(user => user.id !== authUserId);
        otherUsers.sort((a, b) => a.name.localeCompare(b.name));
        itemsList = [authUserProfile, ...otherUsers].filter(user => !!user);
      } else if (recipientsType === RecipientType.Accounts) {
        itemsList = getSortedAccounts(itemsList as RecipientListItem[]);
      } else if (recipientsType === RecipientType.Contacts) {
        itemsList.sort((a, b) => a.name?.localeCompare(b?.name));
      }
      setItems(itemsList);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderComponent = (component: JSX.Element): JSX.Element => {
    const titleByType = {
      [RecipientType.Groups]: 'messages_recipient_groups',
      [RecipientType.Users]: 'messages_recipient_users',
      [RecipientType.Accounts]: 'messages_recipient_accounts',
      [RecipientType.Contacts]: 'messages_recipient_contacts',
    };
    if (havePrebuiltLayout) {
      return component;
    } else {
      return (
        <Layout
          backBtn
          to={prevPath ?? '/'}
          backBtnCallBack={e => {
            e.preventDefault();
            navigate(-1);
          }}
          isMessageLayout
          message={
            recipientsType
              ? (titleByType[recipientsType] ?? titleByType[RecipientType.Groups])
              : undefined
          }
        >
          <SPage>{component}</SPage>
        </Layout>
      );
    }
  };

  return renderComponent(
    <div>
      {loading && <Loader />}
      {!loading && (
        <SContainer>
          {[...items].map((item: RecipientListItem, index: number) => (
            <SRecipientContainer isLastItem={index === items.length - 1} key={item.id}>
              {haveImage && (
                <SProfilePictureContainer>
                  <ProfilePicture diameter={38} profilePictureFileName={item.imagePath} />
                </SProfilePictureContainer>
              )}

              <div className="textContainer">
                <p className="title">{getDisplayedName(item)}</p>
                {!!item.subtitle && <p className="subtitle">{item.subtitle}</p>}
              </div>
            </SRecipientContainer>
          ))}
        </SContainer>
      )}
    </div>
  );
};
